import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";

import SectionHead from "./SectionHead";
import useModalDialog from "../hooks/useModalDialog";
import { plates } from "../data/plates";
import { revealContainer, fadeUp } from "../lib/motionVariants";

const stats = [
  { value: "5+", label: "Years in production" },
  { value: "0%", label: "Commission on sales" },
  { value: "1:1", label: "Built with foundries" },
];

const fullSrc = (plate) => require(`../images/${plate.src}`);

const preloaded = new Set();
function preloadFull(plate) {
  if (preloaded.has(plate.src)) return;
  preloaded.add(plate.src);
  const img = new Image();
  img.src = fullSrc(plate);
}

export default function InUse() {
  const [index, setIndex] = useState(null);
  const isOpen = index !== null;

  const move = useCallback((d) => {
    setIndex((i) => (i + d + plates.length) % plates.length);
  }, []);

  const close = useCallback(() => setIndex(null), []);
  const dialogRef = useModalDialog(isOpen, close);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "ArrowLeft") move(-1);
      if (e.key === "ArrowRight") move(1);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, move]);

  // Warm the next/previous full-res images so Prev/Next paging never shows a load.
  useEffect(() => {
    if (!isOpen) return;
    preloadFull(plates[(index + 1) % plates.length]);
    preloadFull(plates[(index - 1 + plates.length) % plates.length]);
  }, [isOpen, index]);

  const selected = isOpen ? plates[index] : null;

  return (
    <motion.section
      id="in-use"
      variants={revealContainer}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.2 }}
      className="pb-7 pt-10 md:pb-12 md:pt-20"
    >
      <motion.div variants={fadeUp}>
        <SectionHead num="02" title="IN PRODUCTION" meta="reset-type.com ↗" metaHref="https://reset-type.com" />
      </motion.div>

      <div className="grid grid-cols-1 gap-6 py-6 md:grid-cols-2 md:gap-16 md:py-10">
        <motion.p variants={fadeUp} className="max-w-[34ch] text-[clamp(20px,2.2vw,30px)] leading-tight">
          Reset Type Studio has run its whole business on the platform for years — site, catalog,
          specimens and sales.
        </motion.p>
        <motion.dl variants={fadeUp} className="grid grid-cols-3 gap-3 self-start sm:gap-6">
          {stats.map((s) => (
            <div key={s.value}>
              <dt className="font-reactor text-[clamp(30px,3.2vw,44px)] font-bold leading-none [font-variation-settings:'wdth'_6]">
                {s.value}
              </dt>
              <dd className="mt-2 font-mono text-[10.5px] uppercase tracking-[0.14em] text-muted">{s.label}</dd>
            </div>
          ))}
        </motion.dl>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 md:gap-7">
        {plates.map((plate, i) => (
          <motion.figure key={plate.src} variants={fadeUp} className="flex flex-col gap-2">
            <button
              type="button"
              onClick={() => setIndex(i)}
              onMouseEnter={() => preloadFull(plate)}
              onFocus={() => preloadFull(plate)}
              onTouchStart={() => preloadFull(plate)}
              className="block w-full border border-ink bg-surface p-1.5 text-left transition-colors duration-200 hover:border-primary"
            >
              <img
                src={require(`../images/${plate.thumb}`)}
                alt={plate.alt}
                width={880}
                height={442}
                loading="lazy"
                decoding="async"
                className="block h-auto w-full"
              />
            </button>
            <figcaption className="flex justify-between gap-3 font-mono text-[10.5px] uppercase tracking-[0.12em] text-muted">
              <span>{plate.caption}</span>
              <span className="flex gap-3">
                {plate.href && (
                  <a
                    href={plate.href}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary transition-colors duration-200 hover:text-ink"
                  >
                    View live ↗
                  </a>
                )}
                <span className="text-primary">Enlarge ⤢</span>
              </span>
            </figcaption>
          </motion.figure>
        ))}
      </div>

      {isOpen && (
        <div
          ref={dialogRef}
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          aria-label={selected.alt}
          onClick={close}
          className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-[18px] bg-ink-900/95 p-5 outline-none md:p-12"
        >
          <div className="flex w-full max-w-[1200px] flex-wrap items-center justify-between gap-x-5 gap-y-2 font-mono text-[11px] uppercase tracking-[0.16em] text-paper">
            <span>
              {String(index + 1).padStart(2, "0")} / {String(plates.length).padStart(2, "0")} — {selected.caption}
            </span>
            <span className="flex gap-4">
              {selected.href && (
                <a
                  href={selected.href}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="transition-colors duration-200 hover:text-primary-300"
                >
                  View live ↗
                </a>
              )}
              <button type="button" onClick={(e) => { e.stopPropagation(); move(-1); }} className="transition-colors duration-200 hover:text-primary-300">← Prev</button>
              <button type="button" onClick={(e) => { e.stopPropagation(); move(1); }} className="transition-colors duration-200 hover:text-primary-300">Next →</button>
              <button type="button" onClick={(e) => { e.stopPropagation(); close(); }} className="transition-colors duration-200 hover:text-primary-300">Close ✕</button>
            </span>
          </div>

          <img
            key={index}
            src={fullSrc(selected)}
            alt={selected.alt}
            draggable={false}
            onClick={(e) => e.stopPropagation()}
            className="plate-fade max-h-[74vh] w-auto max-w-full border border-paper object-contain"
          />

          <p className="max-w-[70ch] text-center font-mono text-[11px] uppercase tracking-[0.14em] text-paper/60">
            {selected.alt}
          </p>
        </div>
      )}
    </motion.section>
  );
}
